import { useCallback, useEffect, useMemo, useState } from 'react';
import { BookEngineNav } from '../components/editor/BookEngineNav';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEntities } from '../api/queries/entities';
import type { MentionItem } from '../components/editor/extensions/MentionSuggestionList';
import {
  useBook,
  useChapters,
  useParagraphs,
  useCreateChapter,
  type ParagraphDto,
} from '../api/queries/books';
import { useBookStore } from '../stores/book.store';
import { useSelectionStore } from '../stores/selection.store';
import { useShortcut } from '../lib/keybindings';
import { useUiStore } from '../stores/ui.store';
import { FocusMode } from '../components/common/FocusMode';
import { AIToolbarButton } from '../components/ai/AIToolbarButton';
import { ChapterOutline } from '../components/editor/ChapterOutline';
import { ParagraphList } from '../components/editor/ParagraphList';
import { ParagraphContextMenu } from '../components/editor/ParagraphContextMenu';
import { TipTapEditor } from '../components/editor/TipTapEditor';
import { MetadataInheritancePanel } from '../components/editor/MetadataInheritancePanel';
import { PreviewPanel } from '../components/editor/PreviewPanel';
import { ReaderPreview } from '../components/editor/ReaderPreview';
import { BookHealthDashboard } from '../components/editor/BookHealthDashboard';
import { RelevantContextDrawer } from '../components/ai/RelevantContextDrawer';
import { GenerationModal } from '../components/ai/GenerationModal';
import { SlashCommandMenu } from '../components/ai/SlashCommandMenu';
import { CreditsBadge } from '../components/credits/CreditsBadge';
import { WordCount } from '../components/editor/WordCount';
import { VersionHistoryDrawer } from '../components/versions/VersionHistoryDrawer';
import { ImportDialog } from '../components/imports/ImportDialog';
import { DictionaryModal } from '../components/editor/DictionaryModal';
import { DraftPicker } from '../components/editor/DraftPicker';
import { ChapterNarrationDialog } from '../components/editor/ChapterNarrationDialog';
import { BookSettingsDialog } from '../components/editor/BookSettingsDialog';
import { EntityLibrarySidebar } from '../components/entities/EntityLibrarySidebar';
import { useMarkLastEngine } from '../api/queries/bookSession';
import { WorldPanel } from '../components/worlds/WorldPanel';
import { StoryArchitectPanel } from '../components/storyArchitect/StoryArchitectPanel';
import { GenerationHistoryPanel } from '../components/generation/GenerationHistoryPanel';
import { ExportDialog } from '../components/editor/ExportDialog';
import { MiniPlayer } from '../components/editor/MiniPlayer';
import { SceneCompositionView } from '../components/editor/SceneCompositionView';
import { useChapterAudioPlaylist } from '../api/queries/audioPlaylist';
import { usePlaybackStore } from '../stores/playback.store';

type RightPanel =
  | 'metadata'
  | 'preview'
  | 'world'
  | 'entities'
  | 'generations'
  | 'health';

export function BookEditorRoute() {
  const { bookId } = useParams({ strict: false }) as { bookId: string };
  const queryClient = useQueryClient();
  const book = useBook(bookId);
  const chapters = useChapters(bookId);
  const {
    activeChapterId,
    setActiveBook,
    setActiveChapter,
    dirty,
    lastSavedAt,
  } = useBookStore();
  const { paragraphId, select } = useSelectionStore();
  const createChapter = useCreateChapter(bookId);

  const [view, setView] = useState<'editor' | 'read' | 'compose'>('editor');
  const [rightPanel, setRightPanel] = useState<RightPanel>('metadata');
  const [findSimilarOpen, setFindSimilarOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [dictionaryOpen, setDictionaryOpen] = useState(false);
  const [dictionaryQuery, setDictionaryQuery] = useState('');
  const [generationMode, setGenerationMode] = useState<
    'image' | 'audio' | 'text' | null
  >(null);
  const [textPreset, setTextPreset] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [narrationOpen, setNarrationOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [newChapterDialogOpen, setNewChapterDialogOpen] = useState(false);
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const focusMode = useUiStore((s) => s.focusMode);
  const toggleFocusMode = useUiStore((s) => s.toggleFocusMode);
  const closeFocusMode = useCallback(() => {
    if (focusMode) toggleFocusMode();
  }, [focusMode, toggleFocusMode]);

  useEffect(() => {
    setActiveBook(bookId);
    return () => setActiveBook(null);
  }, [bookId, setActiveBook]);

  useMarkLastEngine(bookId, 'editor');

  useEffect(() => {
    if (!activeChapterId && chapters.data && chapters.data.length > 0) {
      setActiveChapter(chapters.data[0]!.id);
    }
  }, [chapters.data, activeChapterId, setActiveChapter]);

  useShortcut({ key: 'n', meta: true, shift: true }, () => {
    setNewChapterTitle('');
    setNewChapterDialogOpen(true);
  });
  useShortcut({ key: 'e', meta: true }, () => setRightPanel('metadata'));
  useShortcut({ key: 'p', meta: true, shift: true }, () =>
    setPreviewMode((prev) => !prev),
  );
  useShortcut({ key: 'r', meta: true }, () => setGenerationMode('image'));
  useShortcut({ key: 'd', meta: true }, () => {
    // Seed with the current text selection so the creator can highlight a name + ⌘D.
    const selection = window.getSelection()?.toString().trim() ?? '';
    setDictionaryQuery(selection);
    setDictionaryOpen(true);
  });
  useShortcut({ key: 'Escape' }, () => {
    setFindSimilarOpen(false);
    setGenerationMode(null);
    setVersionsOpen(false);
    setImportOpen(false);
    setDictionaryOpen(false);
    setPreviewMode(false);
  });
  useShortcut({ key: 'f', meta: true, shift: true }, () => {
    toggleFocusMode();
  });

  const paragraphs = useParagraphs(activeChapterId ?? undefined);

  useEffect(() => {
    if (!paragraphId && paragraphs.data && paragraphs.data.length > 0) {
      select(paragraphs.data[0]!.id);
    }
  }, [paragraphs.data, paragraphId, select]);

  const activeChapter = useMemo(
    () => chapters.data?.find((c) => c.id === activeChapterId),
    [chapters.data, activeChapterId],
  );
  const activeParagraph = useMemo(
    () => paragraphs.data?.find((p) => p.id === paragraphId),
    [paragraphs.data, paragraphId],
  );

  const navigateToParagraph = async (targetId: string) => {
    if (paragraphs.data?.some((p) => p.id === targetId)) {
      select(targetId);
      setTimeout(() => {
        const node =
          document.querySelector(`[data-paragraph-preview="${targetId}"]`) ??
          document.querySelector(`[data-paragraph-id="${targetId}"]`);
        node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }
    for (const chapter of chapters.data ?? []) {
      if (chapter.id === activeChapterId) continue;
      const cached = queryClient.getQueryData<ParagraphDto[]>([
        'paragraphs',
        chapter.id,
      ]);
      if (cached?.some((p) => p.id === targetId)) {
        setActiveChapter(chapter.id);
        select(targetId);
        return;
      }
    }
    for (const chapter of chapters.data ?? []) {
      if (chapter.id === activeChapterId) continue;
      const fresh = await queryClient.fetchQuery<ParagraphDto[]>({
        queryKey: ['paragraphs', chapter.id],
      });
      if (fresh?.some((p) => p.id === targetId)) {
        setActiveChapter(chapter.id);
        select(targetId);
        return;
      }
    }
    // Paragraph not found in any chapter of this book
    console.warn('Target paragraph is outside this book');
  };

  // Entity lookup for @mention in TipTap editor
  const bookEntities = useEntities({
    projectId: bookId,
    scope: 'book',
    pageSize: 200,
  });
  const privateEntities = useEntities({ scope: 'private', pageSize: 200 });
  const entityLookup = useCallback(
    (query: string): MentionItem[] => {
      const all = [
        ...(bookEntities.data?.items ?? []),
        ...(privateEntities.data?.items ?? []),
      ];
      const unique = Array.from(new Map(all.map((e) => [e.id, e])).values());
      const q = query.toLowerCase();
      return unique
        .filter((e) => e.name.toLowerCase().includes(q))
        .slice(0, 10)
        .map((e) => ({ id: e.id, label: e.name }));
    },
    [bookEntities.data, privateEntities.data],
  );

  // Audio playback
  const audioPlaylist = useChapterAudioPlaylist(activeChapterId);
  const loadPlaylist = usePlaybackStore((s) => s.loadPlaylist);
  const playbackPlay = usePlaybackStore((s) => s.play);

  const handlePlayChapter = useCallback(() => {
    if (!activeChapterId || !audioPlaylist.data) return;
    loadPlaylist(
      activeChapterId,
      audioPlaylist.data.items.map((item) => ({
        paragraphId: item.paragraphId,
        audioUrl: item.audioUrl,
        durationMs: item.durationMs,
        speakerLabel: item.speakerLabel,
      })),
    );
    playbackPlay();
  }, [activeChapterId, audioPlaylist.data, loadPlaylist, playbackPlay]);

  const focusWordCount = useMemo(() => {
    if (!paragraphs.data) return 0;
    return paragraphs.data.reduce((total, p) => {
      const text = p.contentHtml.replace(/<[^>]+>/g, '').trim();
      return total + (text ? text.split(/\s+/).length : 0);
    }, 0);
  }, [paragraphs.data]);

  if (book.isLoading) return <CircularProgress />;
  if (book.isError || !book.data)
    return <Typography>Could not load book.</Typography>;

  return (
    <Box>
      {book.data.projectId && (
        <DraftPicker projectId={book.data.projectId} currentBookId={bookId} />
      )}
      <BookEngineNav bookId={bookId} />

      <FocusMode
        open={focusMode}
        onClose={closeFocusMode}
        wordCount={focusWordCount}
      >
        {view === 'editor' && paragraphs.data && activeChapterId && (
          <TipTapEditor
            paragraphs={paragraphs.data}
            chapterId={activeChapterId}
            entityLookup={entityLookup}
          />
        )}
        {view === 'read' && paragraphs.data && (
          <ParagraphList
            paragraphs={paragraphs.data}
            selectedParagraphId={paragraphId}
            onSelect={select}
            bookId={bookId}
            chapterId={activeChapterId ?? undefined}
            onGenerateImage={() => setGenerationMode('image')}
            onGenerateAudio={() => setGenerationMode('audio')}
          />
        )}
        {!paragraphs.data && <CircularProgress size={24} />}
      </FocusMode>
      <Stack direction="row" spacing={2} sx={{ minHeight: '80vh', mt: 1 }}>
        <Paper sx={{ width: 260, p: 2 }}>
          {chapters.data && (
            <ChapterOutline
              bookId={bookId}
              chapters={chapters.data}
              activeChapterId={activeChapterId}
              onSelect={(id) => setActiveChapter(id)}
            />
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 2 }}
          >
            ⌘⇧N new chapter · ⌘E metadata · ⌘⇧P preview · ⌘R image · ⌘D
            dictionary · ⌘/ slash · ⌘S save
          </Typography>
        </Paper>

        <Paper sx={{ flex: 1, p: 3 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            flexWrap="wrap"
          >
            <Typography variant="h5">{book.data.title}</Typography>
            <Chip size="small" label={book.data.status} />
            <Tooltip
              title={
                lastSavedAt
                  ? new Date(lastSavedAt).toLocaleTimeString()
                  : 'not saved yet'
              }
            >
              <Chip
                size="small"
                color={dirty ? 'warning' : 'success'}
                label={dirty ? 'unsaved' : 'saved'}
              />
            </Tooltip>
            <CreditsBadge />
            {paragraphs.data && (
              <WordCount
                paragraphs={paragraphs.data}
                chapterCount={chapters.data?.length}
              />
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              variant={view === 'editor' ? 'contained' : 'outlined'}
              onClick={() => setView('editor')}
            >
              Edit
            </Button>
            <Button
              size="small"
              variant={view === 'read' ? 'contained' : 'outlined'}
              onClick={() => setView('read')}
            >
              Read
            </Button>
            <Button
              size="small"
              variant={view === 'compose' ? 'contained' : 'outlined'}
              onClick={() => setView('compose')}
            >
              Compose
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPreviewMode(true)}
            >
              Preview
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handlePlayChapter}
              disabled={!audioPlaylist.data?.items.some((i) => i.audioUrl)}
            >
              Play Chapter
            </Button>
            <Button size="small" onClick={() => setImportOpen(true)}>
              Import
            </Button>
            <Button size="small" onClick={() => setVersionsOpen(true)}>
              Versions
            </Button>
            <Button size="small" onClick={() => setExportOpen(true)}>
              Export
            </Button>
            <Button size="small" onClick={() => setGenerationMode('image')}>
              Image
            </Button>
            <Button size="small" onClick={() => setGenerationMode('audio')}>
              Audio
            </Button>
            <Button
              size="small"
              onClick={() => setNarrationOpen(true)}
              disabled={!activeChapterId}
            >
              Narrate Chapter
            </Button>
            <Button size="small" onClick={() => setGenerationMode('text')}>
              Text
            </Button>
            <Button size="small" onClick={() => setSettingsOpen(true)}>
              Settings
            </Button>
            <Button
              size="small"
              onClick={() => setFindSimilarOpen(true)}
              disabled={!paragraphId}
            >
              Find similar
            </Button>
            <AIToolbarButton
              paragraphId={paragraphId}
              bookId={bookId}
              chapterId={activeChapterId ?? undefined}
              onImageGenerate={() => setGenerationMode('image')}
              onAudioGenerate={() => setGenerationMode('audio')}
              onTextGenerate={() => setGenerationMode('text')}
              onFindSimilar={() => setFindSimilarOpen(true)}
            />
          </Stack>

          <StoryArchitectPanel bookId={bookId} />

          <Box sx={{ mt: 3 }}>
            {paragraphs.isLoading && <CircularProgress size={18} />}
            <ParagraphContextMenu
              onImageGenerate={() => setGenerationMode('image')}
              onAudioGenerate={() => setGenerationMode('audio')}
              onTextGenerate={() => {
                setTextPreset('');
                setGenerationMode('text');
              }}
              onExpand={() => {
                setTextPreset(
                  'Expand this paragraph with more detail and description.',
                );
                setGenerationMode('text');
              }}
              onRewrite={() => {
                setTextPreset(
                  'Rewrite this paragraph while preserving the core meaning.',
                );
                setGenerationMode('text');
              }}
              onSummarize={() => {
                setTextPreset(
                  'Summarize this paragraph into a concise version.',
                );
                setGenerationMode('text');
              }}
              onFindSimilar={() => setFindSimilarOpen(true)}
            >
              {view === 'editor' && paragraphs.data && activeChapterId && (
                <SlashCommandMenu
                  paragraphId={paragraphId}
                  bookId={bookId}
                  chapterId={activeChapterId ?? undefined}
                  contextParams={{
                    entity_type: 'paragraph',
                    entity_id: paragraphId,
                    book_id: bookId,
                  }}
                  onImageGenerate={() => setGenerationMode('image')}
                  onAudioGenerate={() => setGenerationMode('audio')}
                  onExpand={() => {
                    setTextPreset('Expand this paragraph...');
                    setGenerationMode('text');
                  }}
                  onRewrite={() => {
                    setTextPreset('Rewrite this paragraph...');
                    setGenerationMode('text');
                  }}
                  onContinue={() => {
                    setTextPreset(
                      'Continue writing from where this paragraph ends...',
                    );
                    setGenerationMode('text');
                  }}
                  onSummarize={() => {
                    setTextPreset(
                      'Summarize this paragraph into a concise version...',
                    );
                    setGenerationMode('text');
                  }}
                >
                  {paragraphs.data.length > 200 ? (
                    <ParagraphList
                      paragraphs={paragraphs.data}
                      selectedParagraphId={paragraphId}
                      onSelect={select}
                      bookId={bookId}
                      onGenerateImage={() => setGenerationMode('image')}
                      onGenerateAudio={() => setGenerationMode('audio')}
                    />
                  ) : (
                    <TipTapEditor
                      paragraphs={paragraphs.data}
                      chapterId={activeChapterId}
                      entityLookup={entityLookup}
                    />
                  )}
                </SlashCommandMenu>
              )}
              {view === 'read' && paragraphs.data && (
                <ParagraphList
                  paragraphs={paragraphs.data}
                  selectedParagraphId={paragraphId}
                  onSelect={select}
                  bookId={bookId}
                  chapterId={activeChapterId ?? undefined}
                  onGenerateImage={() => setGenerationMode('image')}
                  onGenerateAudio={() => setGenerationMode('audio')}
                />
              )}
              {view === 'compose' && paragraphs.data && (
                <SceneCompositionView
                  paragraphs={paragraphs.data}
                  bookId={bookId}
                  onGenerateImage={() => setGenerationMode('image')}
                  onGenerateAudio={() => setGenerationMode('audio')}
                />
              )}
            </ParagraphContextMenu>
          </Box>
        </Paper>

        <Box sx={{ width: 360 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant={rightPanel === 'metadata' ? 'contained' : 'outlined'}
              onClick={() => setRightPanel('metadata')}
            >
              Metadata
            </Button>
            <Button
              size="small"
              variant={rightPanel === 'preview' ? 'contained' : 'outlined'}
              onClick={() => setRightPanel('preview')}
            >
              Preview
            </Button>
            <Button
              size="small"
              variant={rightPanel === 'world' ? 'contained' : 'outlined'}
              onClick={() => setRightPanel('world')}
            >
              World
            </Button>
            <Button
              size="small"
              variant={rightPanel === 'entities' ? 'contained' : 'outlined'}
              onClick={() => setRightPanel('entities')}
            >
              Entities
            </Button>
            <Button
              size="small"
              variant={rightPanel === 'generations' ? 'contained' : 'outlined'}
              onClick={() => setRightPanel('generations')}
            >
              History
            </Button>
            <Button
              size="small"
              variant={rightPanel === 'health' ? 'contained' : 'outlined'}
              onClick={() => setRightPanel('health')}
            >
              Health
            </Button>
          </Stack>
          {rightPanel === 'metadata' && (
            <MetadataInheritancePanel
              book={book.data}
              chapter={activeChapter}
              paragraph={activeParagraph}
            />
          )}
          {rightPanel === 'preview' && paragraphs.data && (
            <PreviewPanel
              paragraphs={paragraphs.data}
              selectedParagraphId={paragraphId}
            />
          )}
          {rightPanel === 'world' && <WorldPanel bookId={bookId} />}
          {rightPanel === 'entities' && (
            <EntityLibrarySidebar bookId={bookId} />
          )}
          {rightPanel === 'generations' && (
            <GenerationHistoryPanel paragraphId={paragraphId} />
          )}
          {rightPanel === 'health' && chapters.data && paragraphs.data && (
            <BookHealthDashboard
              bookId={bookId}
              chapters={chapters.data}
              paragraphs={paragraphs.data}
              onNavigateToParagraph={navigateToParagraph}
            />
          )}
        </Box>

        <RelevantContextDrawer
          open={findSimilarOpen}
          onClose={() => setFindSimilarOpen(false)}
          paragraphId={paragraphId ?? undefined}
          bookId={bookId}
          onNavigateToParagraph={navigateToParagraph}
        />
        <GenerationModal
          open={generationMode !== null}
          onClose={() => {
            setGenerationMode(null);
            setTextPreset('');
          }}
          mode={generationMode ?? 'image'}
          paragraphId={paragraphId ?? undefined}
          bookId={bookId}
          chapterId={activeChapterId ?? undefined}
          chapterTitle={activeChapter?.title}
          paragraphText={
            activeParagraph ? stripHtml(activeParagraph.contentHtml) : undefined
          }
          seedPrompt={
            textPreset ||
            (activeParagraph ? stripHtml(activeParagraph.contentHtml) : '')
          }
        />
        <VersionHistoryDrawer
          open={versionsOpen}
          onClose={() => setVersionsOpen(false)}
          bookId={bookId}
        />
        <ImportDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          bookId={bookId}
        />
        <DictionaryModal
          open={dictionaryOpen}
          onClose={() => setDictionaryOpen(false)}
          bookId={bookId}
          initialQuery={dictionaryQuery}
        />
        <ExportDialog
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          bookId={bookId}
          bookTitle={book.data.title}
        />
        {activeChapterId && paragraphs.data && (
          <ChapterNarrationDialog
            open={narrationOpen}
            onClose={() => setNarrationOpen(false)}
            bookId={bookId}
            chapterId={activeChapterId}
            paragraphs={paragraphs.data}
          />
        )}
        <BookSettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          bookId={bookId}
          book={book.data}
        />

        {/* New chapter dialog */}
        <Dialog
          open={newChapterDialogOpen}
          onClose={() => setNewChapterDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>New chapter</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              label="Chapter title"
              value={newChapterTitle}
              onChange={(e) => setNewChapterTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newChapterTitle.trim()) {
                  createChapter.mutate(newChapterTitle.trim());
                  setNewChapterDialogOpen(false);
                  setNewChapterTitle('');
                }
              }}
              fullWidth
              size="small"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewChapterDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!newChapterTitle.trim() || createChapter.isPending}
              onClick={() => {
                if (newChapterTitle.trim()) {
                  createChapter.mutate(newChapterTitle.trim());
                  setNewChapterDialogOpen(false);
                  setNewChapterTitle('');
                }
              }}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {previewMode && chapters.data && activeChapterId && (
          <ReaderPreview
            bookId={bookId}
            chapters={chapters.data}
            currentChapterId={activeChapterId}
            onChapterChange={(chapterId) => setActiveChapter(chapterId)}
            onClose={() => setPreviewMode(false)}
          />
        )}
      </Stack>

      <MiniPlayer />
    </Box>
  );
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '').trim();
}
